import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import api from "../../services/api";
import { Link } from "react-router-dom";
import { useMenuAuthInfo, useMultiLang } from "../../services/GlobalUtil";
import BreadCrumbDiv from "../../components/common/BreadCrumbDiv";
import FunctionButton from "../../components/common/FunctionButton";
import ButtonWrapDiv from "../../components/common/ButtonWrapDiv";
import { useTranslation } from "react-i18next";
import SearchWrapDiv from "../../components/common/SearchWrapDiv";
import CustomDropDownTreeSelect from "../../components/common/CustomDropDownTreeSelect";
import CustomDatePicker from "../../components/common/CustomDatePicker";
import SearchButton from "../../components/common/SearchButton";
import PeriodQuickButtons from "../../components/common/PeriodQuickButtons";
import SupportFilterButtons from "../../components/common/SupportFilterButtons";
import SearchInput from "../../components/common/SearchInput";
import ListWrapDiv from "../../components/common/ListWrapDiv";
import PageBar from "../../components/common/PageBar";
import FullModalDiv from "../../components/common/FullModalDiv";
import PdiDetailPage from "./PdiDetailPage";
import LoadingBoxDiv from "../../components/common/LoadingBoxDiv";
import CodeSelectBox from "../../components/common/CodeSelectBox";

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50].map((n) => ({
  code: n,
  codeDesc: String(n),
}));

const NgCheckListPage = () => {
  const [pageSize, setPageSize] = useState(10);
  const [curPage, setCurPage] = useState(1);
  const [totalCount, setTotalCount] = useState();
  const menuAuthInfo = useMenuAuthInfo();
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(
    moment().subtract(1, "week").add(1, "day").toDate()
  );
  const [endDate, setEndDate] = useState(moment().toDate());
  const [period, setPeriod] = useState("week");
  const sbInspType = useMultiLang("INSPECTION_TYPE");
  const [inspType, setInspType] = useState([]);
  const [vin, setVin] = useState("");
  const [supportFilter, setSupportFilter] = useState("ALL");

  const searchVin = useRef("");
  const searchInspType = useRef([]);
  const searchSupportFilter = useRef("ALL");
  const searchStartDt = useRef(
    moment().subtract(1, "week").add(1, "day").format("YYYY-MM-DD 00:00:00")
  );
  const searchEndDt = useRef(moment().format("YYYY-MM-DD 00:00:00"));

  const [datas, setDatas] = useState([]);

  const [viewId, setViewId] = useState("");
  const [isOpenDetail, setIsOpenDetail] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  useEffect(() => {
    if (menuAuthInfo?.view) {
      loadContentData();
    }
  }, [curPage, pageSize]);

  const handleStartDateChange = (data) => {
    setStartDate(data);
    setPeriod("");
    if (moment(data).isAfter(moment(endDate))) {
      setEndDate(data);
    }
  };

  const handleEndDateChange = (data) => {
    setEndDate(data);
    setPeriod("");
    if (moment(data).isBefore(moment(startDate))) {
      setStartDate(data);
    }
  };

  const handlePeriodSelect = (start, end, key) => {
    setStartDate(start);
    setEndDate(end);
    setPeriod(key);

    searchVin.current = vin;
    searchInspType.current = inspType;
    searchSupportFilter.current = supportFilter;
    searchStartDt.current = moment(start).format("YYYY-MM-DD 00:00:00");
    searchEndDt.current = moment(end).format("YYYY-MM-DD 00:00:00");

    if (curPage === 1) {
      loadContentData();
    } else {
      setCurPage(1);
    }
  };

  const handleVinChange = (e) => {
    setVin(e.target.value.trim());
  };

  const handleSupportFilterSelect = (key) => {
    if (key === supportFilter) {
      return;
    }
    setSupportFilter(key);

    searchVin.current = vin;
    searchInspType.current = inspType;
    searchSupportFilter.current = key;
    searchStartDt.current = moment(startDate).format("YYYY-MM-DD 00:00:00");
    searchEndDt.current = moment(endDate).format("YYYY-MM-DD 00:00:00");

    if (curPage === 1) {
      loadContentData();
    } else {
      setCurPage(1);
    }
  };

  const loadContentData = () => {
    setIsLoading(true);

    api
      .post("/pdi-status/ng-check/view", {
        sVin: searchVin.current,
        sInspTypes: searchInspType.current,
        sSupportFilter: searchSupportFilter.current,
        sStartDate: searchStartDt.current,
        sEndDate: searchEndDt.current,
        page: curPage,
        size: pageSize,
      })
      .then((response) => {
        setDatas(response.data.content);
        setTotalCount(response.data.totalCount);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const pageChange = (data) => {
    if (curPage === data) {
      return;
    }
    setCurPage(data);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurPage(1);
  };

  const handleSearch = () => {
    searchVin.current = vin;
    searchInspType.current = inspType;
    searchSupportFilter.current = supportFilter;
    searchStartDt.current = moment(startDate).format("YYYY-MM-DD 00:00:00");
    searchEndDt.current = moment(endDate).format("YYYY-MM-DD 00:00:00");

    if (curPage === 1) {
      loadContentData();
    } else {
      setCurPage(1);
    }
  };

  const handleDownloadExcel = () => {
    if (isExcelLoading) {
      return;
    }
    setIsExcelLoading(true);
    api
      .post(
        "/pdi-status/ng-check/excel",
        {
          sVin: searchVin.current,
          sInspTypes: searchInspType.current,
          sSupportFilter: searchSupportFilter.current,
          sStartDate: searchStartDt.current,
          sEndDate: searchEndDt.current,
        },
        {
          responseType: "blob",
        }
      )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        link.setAttribute(
          "download",
          "NG_Check_List_" + moment().format("YYYYMMDD") + ".xlsx"
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        setIsExcelLoading(false);
      })
      .catch(() => {
        setIsExcelLoading(false);
      });
  };

  const handleOpenPopup = (viewId) => {
    setIsOpenDetail(true);
    setViewId(viewId);
  };

  return (
    <div>
      <BreadCrumbDiv items={[t("MENU:L0053"), t("MENU:L0267")]}></BreadCrumbDiv>

      <SearchWrapDiv>
        <ButtonWrapDiv>
          <SearchInput
            value={vin}
            onChange={handleVinChange}
            placeholder={"VIN"}
            maxLength={17}
          ></SearchInput>
          <CustomDropDownTreeSelect
            data={sbInspType}
            placeholder={"Inspection Type"}
            onChange={setInspType}
          ></CustomDropDownTreeSelect>
        </ButtonWrapDiv>
        <ButtonWrapDiv className="btn_right">
          <CustomDatePicker
            defaultDate={startDate}
            maxDate={new Date()}
            onChange={(date) => handleStartDateChange(date)}
            dateFormat={"YYYY-MM-dd"}
          ></CustomDatePicker>
          <div>
            <br />
            &nbsp;&nbsp;-&nbsp;&nbsp;
          </div>
          <CustomDatePicker
            defaultDate={endDate}
            maxDate={new Date()}
            onChange={(date) => handleEndDateChange(date)}
            dateFormat={"YYYY-MM-dd"}
          ></CustomDatePicker>

          <SearchButton onClick={handleSearch}></SearchButton>
        </ButtonWrapDiv>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "10px",
          }}
        >
          <SupportFilterButtons
            selected={supportFilter}
            onSelect={handleSupportFilterSelect}
          />
          <PeriodQuickButtons selected={period} onSelect={handlePeriodSelect} />
        </div>
      </SearchWrapDiv>

      <ButtonWrapDiv className="btn_top">
        <FunctionButton
          value={"Excel"}
          onClick={handleDownloadExcel}
          loading={isExcelLoading}
        ></FunctionButton>
      </ButtonWrapDiv>

      <LoadingBoxDiv loading={isLoading}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            marginBottom: "10px",
          }}
        >
          <CodeSelectBox
            value={pageSize}
            codes={PAGE_SIZE_OPTIONS}
            onChange={handlePageSizeChange}
            width="64px"
            style={{
              border: "1px solid #ccc",
              borderRadius: "3px",
              padding: "0 5px",
              height: "28px",
              fontSize: "12px",
            }}
          ></CodeSelectBox>
        </div>

        <ListWrapDiv>
          <table>
            <colgroup>
              <col style={{ width: "5%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "auto" }} />
            </colgroup>
            <thead>
              <tr>
                <th>No.</th>
                <th>VIN</th>
                <th>Car Model</th>
                <th>Car Year</th>
                <th>Car Engine</th>
                <th>Dealer Code</th>
                <th>Inspection Date</th>
                <th>Inspection Type</th>
                <th>Inspection Item</th>
              </tr>
            </thead>
            <tbody>
              {datas.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center" }}>
                    {t("L0094")}
                    <br />
                    {t("L0095")}
                  </td>
                </tr>
              ) : (
                datas.map((data, rowIndex) => (
                  <tr key={rowIndex}>
                    <td>{data.rowNum}</td>
                    <td>
                      {menuAuthInfo.view ? (
                        <Link to="#" onClick={() => handleOpenPopup(data.id)}>
                          {data.vin}
                        </Link>
                      ) : (
                        <span>{data.vin}</span>
                      )}
                    </td>
                    <td>{data.vehicle}</td>
                    <td>{data.carYear}</td>
                    <td>{data.carEngine}</td>
                    <td>{data.loc}</td>
                    <td>
                      {data.inspDate
                        ? moment(data.inspDate).format("YYYY-MM-DD HH:mm:ss")
                        : ""}
                    </td>
                    <td>{data.inspTypeDesc}</td>
                    <td>{data.inspItem}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ListWrapDiv>

        <PageBar
          TotalCount={totalCount}
          CurrentPage={curPage}
          CountPerPage={pageSize}
          MaxPageBarCount={10}
          onPageСhange={pageChange}
        ></PageBar>
      </LoadingBoxDiv>

      <FullModalDiv
        isOpen={isOpenDetail}
        onClose={() => setIsOpenDetail(false)}
      >
        <PdiDetailPage sId={viewId} />
      </FullModalDiv>
    </div>
  );
};

export default NgCheckListPage;
